import { useEffect, useState } from 'react';
import { Copy, Send, Trash2, Webhook as WebhookIcon } from 'lucide-react';
import { OUTGOING_LIMITS, WEBHOOK_EVENTS, WEBHOOK_LIMITS, type OutgoingWebhook, type Webhook, type WebhookDelivery, type WebhookEventName } from '@spawnwire/shared';
import { Button, clsx, EmptyState, Field, Input, Spinner, Switch } from '../../components/ui';
import { errorMessage, http } from '../../lib/api';
import { copyText } from '../../lib/hooks';
import { useStore } from '../../store/store';
import { ui } from '../../store/ui';
import { Section } from './SettingsLayout';

/** What each event is called on screen, grouped. */
export const EVENT_LABELS: Record<WebhookEventName, string> = {
  'message.created': 'A message is sent',
  'message.updated': 'A message is edited',
  'message.deleted': 'A message is deleted',
  'member.joined': 'Someone joins',
  'member.left': 'Someone leaves',
  'member.kicked': 'Someone is kicked',
  'member.banned': 'Someone is banned',
  'member.unbanned': 'Someone is unbanned',
  'channel.created': 'A channel is created',
  'channel.updated': 'A channel is changed',
  'channel.deleted': 'A channel is deleted',
  'role.created': 'A role is created',
  'role.updated': 'A role is changed',
  'role.deleted': 'A role is deleted',
  'event.created': 'An event is scheduled',
  'event.updated': 'An event changes or starts or ends',
  'event.deleted': 'An event is deleted',
};

/** Toggles an event in a list, keeping the order the events are defined in. */
export function toggleEvent(list: WebhookEventName[], event: WebhookEventName, on: boolean): WebhookEventName[] {
  const next = new Set(list);
  if (on) next.add(event);
  else next.delete(event);
  return WEBHOOK_EVENTS.filter((e) => next.has(e));
}

function Secret({ secret, onDone }: { secret: string; onDone: () => void }) {
  return (
    <div className="rounded-lg border border-accent bg-accent-soft p-3" role="status">
      <p className="text-sm font-medium text-fg">Copy the signing secret now. It is shown only this once.</p>
      <p className="mt-1 text-xs text-muted">Use it to check that requests really come from Spawnwire (see the developer docs). If it leaks, replace it.</p>
      <div className="mt-2 flex gap-2">
        <Input readOnly value={secret} aria-label="Signing secret" onFocus={(e) => e.currentTarget.select()} className="font-mono text-xs" />
        <Button
          variant="secondary"
          icon={Copy}
          onClick={() =>
            void copyText(secret).then(
              () => ui.toast('Copied.', 'success'),
              () => ui.toast('Could not copy. Select the secret and copy it by hand.', 'error'),
            )
          }
        >
          Copy
        </Button>
      </div>
      <div className="mt-2 flex justify-end">
        <Button size="sm" variant="ghost" onClick={onDone}>
          I have copied it
        </Button>
      </div>
    </div>
  );
}

function EventPicker({ value, onChange }: { value: WebhookEventName[]; onChange: (v: WebhookEventName[]) => void }) {
  return (
    <fieldset className="grid gap-x-4 sm:grid-cols-2">
      <legend className="sr-only">Events to send</legend>
      {WEBHOOK_EVENTS.map((e) => (
        <label key={e} className="flex items-center gap-2 py-1 text-sm text-fg">
          <input type="checkbox" checked={value.includes(e)} onChange={(ev) => onChange(toggleEvent(value, e, ev.target.checked))} className="size-4 accent-[var(--accent)]" />
          {EVENT_LABELS[e]}
        </label>
      ))}
    </fieldset>
  );
}

function DeliveryLog({ spaceId, id }: { spaceId: string; id: string }) {
  const [log, setLog] = useState<WebhookDelivery[] | null>(null);
  useEffect(() => {
    void http.get<{ data: WebhookDelivery[] }>(`/spaces/${spaceId}/webhook-endpoints/${id}/deliveries?limit=10`).then((r) => setLog(r.data));
  }, [spaceId, id]);
  if (log === null) return <Spinner className="size-4" />;
  if (log.length === 0) return <p className="text-xs text-muted">Nothing has been sent yet. Try “Send a test”.</p>;
  return (
    <ul className="space-y-1 text-xs" aria-label="Recent deliveries">
      {log.map((d) => (
        <li key={d.id} className="flex flex-wrap items-center gap-x-3 text-muted">
          <span className={clsx('font-medium', d.status === 'delivered' ? 'text-success' : d.status === 'failed' ? 'text-danger' : 'text-fg')}>{d.status === 'pending' ? 'retrying' : d.status}</span>
          <span className="text-fg">{d.event}</span>
          {d.lastStatusCode ? <span>answered {d.lastStatusCode}</span> : null}
          {d.lastError && d.status !== 'delivered' ? <span>{d.lastError}</span> : null}
          <span>{d.attempts > 1 ? `${d.attempts} tries` : ''}</span>
        </li>
      ))}
    </ul>
  );
}

function Endpoint({ hook, spaceId, onChanged, onSecret }: { hook: OutgoingWebhook; spaceId: string; onChanged: () => void; onSecret: (s: string) => void }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<'secret' | 'delete' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const url = `/spaces/${spaceId}/webhook-endpoints/${hook.id}`;
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      setConfirm(null);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className="rounded-lg border border-line bg-sidebar p-3" data-testid="outgoing-webhook" aria-label={hook.name}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <WebhookIcon className="size-4 shrink-0 text-muted" aria-hidden />
            <span className="truncate font-medium text-fg">{hook.name}</span>
            {!hook.enabled && <span className="rounded bg-danger-soft px-1.5 py-0.5 text-[10px] font-bold uppercase text-danger">Off</span>}
          </div>
          <p className="mt-0.5 break-all text-xs text-muted">{hook.url}</p>
          <p className="mt-0.5 text-xs text-faint">{hook.events.length} event{hook.events.length === 1 ? '' : 's'}</p>
          {hook.disabledReason && <p className="mt-1 text-xs text-danger">{hook.disabledReason}</p>}
        </div>
        <Switch checked={hook.enabled} onChange={(enabled) => void run(async () => { await http.patch(url, { enabled }); onChanged(); })} label={hook.enabled ? 'On' : 'Off'} disabled={busy} />
      </div>
      <div className="mt-2 flex flex-wrap gap-1">
        <Button size="sm" variant="ghost" icon={Send} disabled={busy || !hook.enabled} onClick={() => void run(async () => { await http.post(`${url}/ping`); ui.toast('Test sent. See the log below.', 'success'); setOpen(true); })}>
          Send a test
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen((o) => !o)}>
          {open ? 'Hide log' : 'Show log'}
        </Button>
        {confirm === null && (
          <>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => setConfirm('secret')}>
              New secret
            </Button>
            <Button size="sm" variant="ghost" icon={Trash2} className="text-danger hover:text-danger" disabled={busy} onClick={() => setConfirm('delete')}>
              Delete
            </Button>
          </>
        )}
      </div>
      {confirm && (
        <div className="mt-2 flex flex-wrap items-center gap-2" role="group" aria-label="Confirm">
          <span className="text-sm text-fg">{confirm === 'secret' ? 'Replace the secret? Checks with the old one will fail.' : 'Delete this webhook?'}</span>
          <Button
            size="sm"
            variant="danger"
            loading={busy}
            onClick={() =>
              void run(async () => {
                if (confirm === 'secret') onSecret((await http.post<OutgoingWebhook>(`${url}/secret`)).secret ?? '');
                else {
                  await http.del(url);
                  onChanged();
                }
              })
            }
          >
            {confirm === 'secret' ? 'Yes, replace it' : 'Yes, delete it'}
          </Button>
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => setConfirm(null)}>
            Keep
          </Button>
        </div>
      )}
      {open && (
        <div className="mt-2 border-t border-line pt-2">
          <DeliveryLog spaceId={spaceId} id={hook.id} />
        </div>
      )}
      {error && (
        <p role="alert" className="mt-2 rounded-md bg-danger-soft px-3 py-2 text-sm text-danger">
          {error}
        </p>
      )}
    </li>
  );
}

/** Space settings: webhooks. Incoming ones are managed in each channel's settings; outgoing ones are here. */
export function SpaceWebhooks({ spaceId }: { spaceId: string }) {
  const channels = useStore((s) => s.channels);
  const [incoming, setIncoming] = useState<Webhook[] | null>(null);
  const [outgoing, setOutgoing] = useState<OutgoingWebhook[] | null>(null);
  const [name, setName] = useState('');
  const [address, setAddress] = useState('https://');
  const [events, setEvents] = useState<WebhookEventName[]>(['message.created']);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);

  const load = () => {
    http.get<{ data: Webhook[] }>(`/spaces/${spaceId}/webhooks`).then((r) => setIncoming(r.data)).catch((err) => setError(errorMessage(err)));
    http.get<{ data: OutgoingWebhook[] }>(`/spaces/${spaceId}/webhook-endpoints`).then((r) => setOutgoing(r.data)).catch((err) => setError(errorMessage(err)));
  };
  useEffect(load, [spaceId]);

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const made = await http.post<OutgoingWebhook>(`/spaces/${spaceId}/webhook-endpoints`, { name: name.trim(), url: address.trim(), events });
      if (made.secret) setSecret(made.secret);
      setName('');
      setAddress('https://');
      load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Section title="Sending to other services" description="An outgoing webhook tells another service when something happens here: it sends a signed request to an address you choose. Only events everyone in the space can see are sent, never a private channel or a conversation.">
        <p className="rounded-md bg-active px-3 py-2 text-xs text-muted">
          Messages sent in channels everyone can see are passed on word for word, along with who sent them, so only add addresses you trust.
        </p>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim() && events.length) void create();
          }}
        >
          <Field label="Name" htmlFor="ow-name">
            <Input id="ow-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={WEBHOOK_LIMITS.name} placeholder="Build server" />
          </Field>
          <Field label="Address" htmlFor="ow-url" hint="Must start with https:// and be reachable from the internet.">
            <Input id="ow-url" value={address} onChange={(e) => setAddress(e.target.value)} maxLength={OUTGOING_LIMITS.url} inputMode="url" />
          </Field>
          <Field label="Send me">
            <EventPicker value={events} onChange={setEvents} />
          </Field>
          <Button type="submit" loading={busy} disabled={!name.trim() || events.length === 0 || address.trim().length < 9}>
            Add webhook
          </Button>
        </form>
        {error && (
          <p role="alert" className="rounded-md bg-danger-soft px-3 py-2 text-sm text-danger">
            {error}
          </p>
        )}
      </Section>
      <Section title="Your outgoing webhooks">
        {secret && <Secret secret={secret} onDone={() => setSecret(null)} />}
        {outgoing === null ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : outgoing.length === 0 ? (
          <EmptyState icon={WebhookIcon} title="None yet" body="Add one above to start sending events." />
        ) : (
          <ul className="space-y-2">
            {outgoing.map((h) => (
              <Endpoint key={h.id} hook={h} spaceId={spaceId} onChanged={load} onSecret={setSecret} />
            ))}
          </ul>
        )}
      </Section>
      <Section title="Posting into channels" description="Incoming webhooks let other services post here. Make and change them in a channel's settings, under Webhooks.">
        {incoming === null ? (
          <Spinner className="size-4" />
        ) : incoming.length === 0 ? (
          <p className="text-sm text-muted">No incoming webhooks.</p>
        ) : (
          <ul className="space-y-1 text-sm" aria-label="Incoming webhooks">
            {incoming.map((h) => (
              <li key={h.id} className="flex items-center justify-between gap-2 rounded-md bg-sidebar px-3 py-2">
                <span className="truncate text-fg">{h.name}</span>
                <span className="shrink-0 text-xs text-muted">#{channels[h.channelId]?.name ?? 'a channel'}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </>
  );
}
